import React from "react";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";

// Same letterheads as the voucher print (VoucherPrintPreview), MOMBASA fallback.
const LOGO_BY_BRANCH = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

const money = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const day = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

/**
 * Printable petty cash refill request (2026-09-17). Only offered once approved.
 *
 * ISSUED is the normal approved state since Batch141 - approval credits the fund in the same
 * act. A plain APPROVED row is one of the 12 legacy requests approved "for the record only"
 * (Batch142) and was never credited, so the stamp says exactly that rather than implying
 * money moved.
 *
 * Rendered on screen and, via renderToStaticMarkup, into the print popup - inline styles only.
 */
const RefillPrintPreview = ({ refill, signatureImageUrl }) => {
  if (!refill) return null;

  const logo = LOGO_BY_BRANCH[refill.branchCode] || logoMombasa;
  const credited = refill.status === "ISSUED";
  const amount = refill.actualAmountIssued ?? refill.requestedAmount;
  const stamp = credited ? "APPROVED - CREDITED TO FUND" : "APPROVED - NOT CREDITED";
  const stampColor = credited ? "#1a7f43" : "#8a6d1f";

  const cell = { padding: "6px 8px", verticalAlign: "top", fontSize: "12px" };
  const label = { ...cell, width: "160px", color: "#555", fontWeight: 600 };
  const line = { borderBottom: "1px solid #000", height: "34px" };

  return (
    <div
      style={{
        width: "190mm",
        minHeight: "128mm",
        margin: "0 auto",
        padding: "12mm",
        background: "#fff",
        color: "#000",
        fontFamily: "Arial, Helvetica, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <img src={logo} alt="" style={{ height: "62px", objectFit: "contain" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "17px", fontWeight: 700, letterSpacing: "1px" }}>PETTY CASH REFILL</div>
          <div style={{ fontSize: "13px", marginTop: "2px" }}>
            No. <strong>{refill.requestNumber}</strong>
          </div>
          <div style={{ fontSize: "11px", color: "#555" }}>{refill.branchCode}</div>
        </div>
      </div>

      <div style={{ marginTop: "10px" }}>
        <span
          style={{
            border: `2px solid ${stampColor}`,
            color: stampColor,
            padding: "2px 10px",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "1px",
            borderRadius: "3px",
          }}
        >
          {stamp}
        </span>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px", border: "1px solid #000" }}>
        <tbody>
          <tr>
            <td style={label}>Fund</td>
            <td style={cell}>{refill.fundName || "-"}</td>
            <td style={label}>Requested on</td>
            <td style={cell}>{day(refill.requestedDate)}</td>
          </tr>
          <tr>
            <td style={label}>Requested by</td>
            <td style={cell}>{refill.requestedByName || "-"}</td>
            <td style={label}>Approved on</td>
            <td style={cell}>{day(refill.approvedDate)}</td>
          </tr>
          <tr>
            <td style={label}>Reason</td>
            <td style={cell} colSpan={3}>
              {refill.reason || "-"}
            </td>
          </tr>
          <tr>
            <td style={label}>Approved float</td>
            <td style={cell}>KES {money(refill.approvedFloatAtRequest)}</td>
            <td style={label}>Balance at request</td>
            <td style={cell}>KES {money(refill.currentBalanceAtRequest)}</td>
          </tr>
          <tr>
            <td style={label}>{credited ? "Amount credited" : "Amount approved"}</td>
            <td style={{ ...cell, fontSize: "16px", fontWeight: 700 }} colSpan={3}>
              KES {money(amount)}
            </td>
          </tr>
          {refill.paymentReference && (
            <tr>
              <td style={label}>Payment reference</td>
              <td style={cell} colSpan={3}>
                {refill.paymentReference}
              </td>
            </tr>
          )}
          {refill.approvalComment && (
            <tr>
              <td style={label}>Approval comment</td>
              <td style={cell} colSpan={3}>
                {refill.approvalComment}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* "Approved by" carries the General Manager's seal - the same company mark the
          voucher and the quotation print. signatureImageUrl is a data: URI (the print popup
          has no auth and no base URL); without one the line simply prints blank. */}
      <div style={{ display: "flex", gap: "18px", marginTop: "26px" }}>
        {["Requested by", "Received by", "Approved by"].map((sig) => {
          const isApproval = sig === "Approved by";
          const name = sig === "Requested by" ? refill.requestedByName : isApproval ? refill.approvedByName : null;
          return (
            <div key={sig} style={{ flex: 1 }}>
              <div style={{ height: "48px", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                {isApproval && signatureImageUrl && (
                  <img src={signatureImageUrl} alt="" style={{ maxHeight: "48px", maxWidth: "100%", objectFit: "contain" }} />
                )}
              </div>
              <div style={line} />
              <div style={{ fontSize: "10.5px", color: "#555", marginTop: "3px" }}>
                {sig}
                {name ? ` · ${name}` : ""}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "14px", fontSize: "9.5px", color: "#777", textAlign: "center" }}>
        {refill.requestNumber} &middot; printed {new Date().toLocaleString("en-GB")}
      </div>
    </div>
  );
};

export default RefillPrintPreview;
